import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { TransactionCategory, useBillingContext, useEditPaymentMethodContext } from '@shipstation/financial-services-ui-components';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import { emptyObject } from 'ui-components/default-props/empty';
import { PaymentMethod } from '../../../../../../enums/payment-method';
import { updatePaymentMethod } from '../../../../../../financial-services-ui/api';
import { mapBillingInfoDataToBillingAddress, getBillingInfoFromPaymentMethod } from '../../../../../../financial-services-ui/mapping';
import { LogService } from '../../../../../../services/log-service';
import { ManageModal } from '../manage-modal';
import { EditPaymentMethodBody } from './edit-payment-method-body';
import { getSellerLight } from '../../../../../../store/seller/seller.actions';
import { useWallet } from '../../../../../../wallet/hooks/use-wallet';
import { TaxFormContextProvider, useTaxFormContext } from '../../../context/tax-form-context-provider';
import { PageEventSourceStrings } from '../../../hooks/use-track-event';

const showNotification = (type, content) => {
  NotificationService.showNotification({
    notificationType: type,
    title: <FormattedMessage {...messages.header} />,
    content: <FormattedMessage {...content} />,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  });
};

/** @typedef {import('../../../../../../financial-services-ui/api').UpdatePaymentMethodBody} UpdatePaymentMethodBody */

/**
 * @param {object} props
 * @param {() => void} props.onCancel
 * @param {() => void} props.onConfirm
 * @returns {JSX.Element}
 */
const EditPaymentMethodModalContent = ({ onCancel, onConfirm }) => {
  const dispatch = useDispatch();
  const { seller } = useSelector((state) => state);
  const { setBillingInfo, isBillingInfoValid, billingInfo } = useBillingContext();
  const [performFormValidation, setPerformFormValidation] = useState();
  const { nickname, nicknameError, nameOnCard, nameOnCardError, expiryDate, expiryDateError, paymentMethod } =
    useEditPaymentMethodContext();
  const [assignedPreferences, setAssignedPreferences] = useState(/** @type {TransactionCategory[]} */ (paymentMethod.assignedPreferences));
  const [isLoading, setIsLoading] = useState(false);
  const wallet = useWallet();
  const { paymentMethod: paymentMethodType } = paymentMethod;
  const isPaymentMethodAch = paymentMethodType === PaymentMethod.ACH;

  const { submitTaxForm, isTaxSectionOpen, isEditing: isEditingTaxForm } = useTaxFormContext();

  // Store latest values in ref so handleOnSubmit always reads fresh values
  const taxFormStateRef = useRef({ isTaxSectionOpen, isEditingTaxForm, submitTaxForm });
  taxFormStateRef.current = { isTaxSectionOpen, isEditingTaxForm, submitTaxForm };

  useEffect(() => {
    if (!paymentMethod?.accountHolderAddress) {
      setBillingInfo({}, false);
    } else {
      const paymentMethodInfo = getBillingInfoFromPaymentMethod(paymentMethod, seller ?? emptyObject);
      setBillingInfo(paymentMethodInfo, true);
    }
  }, [paymentMethod, setBillingInfo, seller]);

  const setPerformValidationFunction = useCallback((fn) => {
    setPerformFormValidation(fn);
  }, []);

  const handleOnCancel = useCallback(() => {
    setBillingInfo(null, false);
    onCancel();
  }, [onCancel, setBillingInfo]);

  const isInvalidData = useMemo(() => {
    const hasNicknameError = nicknameError || !nickname;
    if (hasNicknameError) return true;
    const hasBillingInfoError = !isBillingInfoValid;
    if (hasBillingInfoError) return true;
    if (isPaymentMethodAch) return false;
    const hasCreditCardInfoError = !nameOnCard || nameOnCardError || !expiryDate || expiryDateError;
    return hasCreditCardInfoError;
  }, [expiryDate, expiryDateError, isBillingInfoValid, isPaymentMethodAch, nameOnCard, nameOnCardError, nickname, nicknameError]);

  const handleOnSubmit = useCallback(async () => {
    performFormValidation?.();
    if (isInvalidData) {
      return null;
    }
    setIsLoading(true);

    const billingAddress = mapBillingInfoDataToBillingAddress(billingInfo);
    const filteredPreferences = assignedPreferences.filter(
      (category) => category !== TransactionCategory.AddFunds || wallet?.isWalletRegistered,
    );

    /** @type {UpdatePaymentMethodBody} */
    const payload = {
      paymentMethodId: paymentMethod.id,
      infoUpdateRequest: {
        nickname,
      },
      assignedPreferences: filteredPreferences,
    };

    const adjustedDate = new Date(expiryDate);
    adjustedDate.setDate(3); // set to third of month as a hack since we just care about month/year and this works with all time zones

    payload.infoUpdateRequest = {
      ...payload.infoUpdateRequest,
      billingAddress,
    };

    if (!isPaymentMethodAch) {
      payload.infoUpdateRequest.expirationDate = adjustedDate;
      payload.infoUpdateRequest.holderName = nameOnCard;
    }

    if (filteredPreferences.includes(TransactionCategory.AddFunds)) {
      payload.walletUpdateRequest = {
        walletId: Number(wallet?.walletId),
      };
    }

    try {
      const result = await updatePaymentMethod(payload);

      if (!result?.success) {
        showNotification(NOTIFICATION_TYPE_ERROR, messages.editError);
        return;
      }

      const { isTaxSectionOpen: isSectionOpen, isEditingTaxForm: isEditing, submitTaxForm: submitForm } = taxFormStateRef.current;
      if (isEditing && isSectionOpen) {
        submitForm();
      }

      dispatch(getSellerLight());
      showNotification(NOTIFICATION_TYPE_SUCCESS, messages.editSuccess);
      onConfirm();
    } catch (error) {
      LogService.logError(new Error('Error editing payment method', { cause: error }));
      showNotification(NOTIFICATION_TYPE_ERROR, messages.editError);
    } finally {
      setIsLoading(false);
    }
  }, [
    performFormValidation,
    isInvalidData,
    billingInfo,
    assignedPreferences,
    paymentMethod.id,
    nickname,
    expiryDate,
    isPaymentMethodAch,
    wallet?.isWalletRegistered,
    wallet?.walletId,
    nameOnCard,
    dispatch,
    onConfirm,
  ]);

  return (
    <ManageModal
      actionMessage={messages.saveButton}
      cancelMessage={messages.cancelButton}
      headerMessage={messages.header}
      isActionDisabled={isLoading}
      onAction={handleOnSubmit}
      onCancel={handleOnCancel}
      onClose={handleOnCancel}
    >
      <EditPaymentMethodBody onUsageSelectorChange={setAssignedPreferences} setPerformFormValidation={setPerformValidationFunction} />
    </ManageModal>
  );
};

export const EditPaymentMethodModal = (props) => (
  <TaxFormContextProvider source={PageEventSourceStrings.EDIT_PAYMENT_METHOD}>
    <EditPaymentMethodModalContent {...props} />
  </TaxFormContextProvider>
);
